import type { IndicatifCatalogue, LieuxCatalogue, VilleCatalogue } from '@/api/onboarding'

/**
 * Suggestions of the number screen, computed in the browser from the catalogue
 * the server sends once (callizy_backend/telephony/lieux_numeros.py). Typing
 * never calls the server, let alone the providers: only choosing a suggestion
 * starts a number search.
 *
 * « 514 », « Laval », « st jerome », « 514 dor » (Dollard-des-Ormeaux, Dorval).
 */

export type Suggestion =
  | {
      type: 'indicatif'
      lieu: string
      code: string
      province: string
      villes: string[]
    }
  | {
      type: 'ville'
      lieu: string
      nom: string
      province: string
      indicatifs: string[]
    }

/** « Saint-Jérôme », « st jerome », « ST. JÉRÔME » -> « saint jerome ». Same rule as the server. */
export function normaliser(texte: string): string {
  const abreges: Record<string, string> = {
    st: 'saint',
    ste: 'sainte',
    mt: 'mont',
  }
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((m) => abreges[m] ?? m)
    .join(' ')
}

/** 0: the name starts with it, 1: a word of the name does, 2: its letters in order (dor -> Dollard). */
function score(nom: string, texte: string): number | null {
  const n = normaliser(nom)
  if (n.startsWith(texte)) return 0
  if (n.split(' ').some((mot) => mot.startsWith(texte))) return 1
  const cherche = texte.replace(/ /g, '')
  if (cherche.length < 2 || n[0] !== cherche[0]) return null
  let i = 0
  for (const c of n.replace(/ /g, '')) if (c === cherche[i]) i++
  return i === cherche.length ? 2 : null
}

function suggestionIndicatif(i: IndicatifCatalogue): Suggestion {
  return {
    type: 'indicatif',
    lieu: i.code,
    code: i.code,
    province: i.province,
    villes: i.villes,
  }
}

function suggestionVille(v: VilleCatalogue): Suggestion {
  return {
    type: 'ville',
    lieu: v.id,
    nom: v.nom,
    province: v.province,
    indicatifs: v.indicatifs,
  }
}

export function suggerer(
  catalogue: LieuxCatalogue | undefined,
  saisie: string,
  pays: 'CA' | 'US' = 'CA',
  max = 6,
): Suggestion[] {
  const texte = normaliser(saisie)
  if (!texte) return []
  const [premier, ...reste] = texte.split(' ')
  const lesVilles = (catalogue?.villes ?? []).filter((v) => v.pays === pays)

  // Digits first: an area code, complete or not, possibly followed by a city.
  if (/^\d+$/.test(premier)) {
    if (premier.length > 3) return []
    const codes = (catalogue?.indicatifs ?? []).filter((i) => i.pays === pays && i.code.startsWith(premier))
    if (premier.length < 3) return codes.slice(0, max).map(suggestionIndicatif)
    const indicatif = codes[0]
    const villes = lesVilles.filter((v) => v.indicatifs.includes(premier))
    const suite = reste.join(' ')
    if (suite) return classer(villes, suite).slice(0, max).map(suggestionVille)
    // A US or unknown code still searches, on its own.
    const tete: Suggestion = indicatif
      ? suggestionIndicatif(indicatif)
      : {
          type: 'indicatif',
          lieu: premier,
          code: premier,
          province: '',
          villes: [],
        }
    return [tete, ...villes.slice(0, max - 1).map(suggestionVille)]
  }

  return classer(lesVilles, texte).slice(0, max).map(suggestionVille)
}

function classer(villes: VilleCatalogue[], texte: string): VilleCatalogue[] {
  const notes: { v: VilleCatalogue; s: number; i: number }[] = []
  villes.forEach((v, i) => {
    const s = Math.min(...[v.nom, ...v.autres_noms].map((nom) => score(nom, texte) ?? 9))
    if (s < 9) notes.push({ v, s, i })
  })
  // Equal scores keep the catalogue order: the biggest cities first.
  return notes.sort((a, b) => a.s - b.s || a.i - b.i).map((n) => n.v)
}

/** What the search field shows once a place is chosen: « Dorval, QC » or « 514 ». */
export function libelleLieu(catalogue: LieuxCatalogue | undefined, lieu: string): string {
  if (!lieu || /^\d{3}$/.test(lieu)) return lieu
  const v = catalogue?.villes.find((x) => x.id === lieu)
  return v ? `${v.nom}, ${v.province}` : ''
}

/**
 * The first place proposed, before the visitor types anything. Free signals
 * only: the browser time zone gives the province (Montréal users often run
 * on America/Toronto, so the language separates Montréal from Toronto).
 */
const PAR_FUSEAU: Record<string, string> = {
  'America/Vancouver': 'bc-vancouver',
  'America/Edmonton': 'ab-calgary',
  'America/Yellowknife': 'nt-yellowknife',
  'America/Whitehorse': 'yt-whitehorse',
  'America/Winnipeg': 'mb-winnipeg',
  'America/Regina': 'sk-saskatoon',
  'America/Swift_Current': 'sk-saskatoon',
  'America/Halifax': 'ns-halifax',
  'America/Glace_Bay': 'ns-halifax',
  'America/Moncton': 'nb-moncton',
  'America/St_Johns': 'nl-saint-john-s',
}

export function lieuDeDepart(langue: string): string {
  let fuseau = ''
  try {
    fuseau = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    // Old browser: the language alone decides.
  }
  return PAR_FUSEAU[fuseau] ?? (langue.startsWith('fr') ? 'qc-montreal' : 'on-toronto')
}

/**
 * The place of the last number chosen. When that number cannot be bought (taken
 * between the choice and the card, sometimes days later), the number screen
 * resumes the search there. Storage can fail (private browsing): never blocking.
 */
const CLE_LIEU = 'callizy_lieu_numero'

export function garderLieu(lieu: string): void {
  try {
    if (lieu) localStorage.setItem(CLE_LIEU, lieu)
  } catch {
    // Private browsing: the area code of the lost number is the fallback.
  }
}

export function lieuGarde(): string | undefined {
  try {
    return localStorage.getItem(CLE_LIEU) || undefined
  } catch {
    return undefined
  }
}
